import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FaSearch, FaFilter, FaTimes, FaTh, FaList, FaSortAmountDown,
  FaHeart, FaMapMarkerAlt, FaBriefcase, FaGraduationCap,
  FaChevronLeft, FaChevronRight, FaLock, FaCheckCircle, FaUserCircle
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Skeleton Card
const SkeletonCard = () => (
  <div className="bg-white rounded-xl shadow-md overflow-hidden animate-pulse">
    <div className="h-56 bg-gray-200" />
    <div className="p-4 space-y-3">
      <div className="h-5 bg-gray-200 rounded w-3/4" />
      <div className="h-4 bg-gray-200 rounded w-1/2" />
      <div className="h-4 bg-gray-200 rounded w-2/3" />
      <div className="h-4 bg-gray-200 rounded w-1/3" />
      <div className="h-10 bg-gray-200 rounded mt-4" />
    </div>
  </div>
);

// Profile Card
const ProfileCard = ({ profile, isPremium, viewMode }) => {
  const isList = viewMode === 'list';

  return (
    <div className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-[#DC143C]/30 group ${isList ? 'flex' : ''}`}>
      {/* Photo */}
      <div className={`relative overflow-hidden ${isList ? 'w-40 flex-shrink-0' : 'h-56'}`}>
        {profile.photos && profile.photos.length > 0 ? (
          <img
            src={profile.photos[0]}
            alt={profile.name}
            className={`w-full object-cover group-hover:scale-105 transition-transform duration-500 ${isList ? 'h-full' : 'h-56'}`}
          />
        ) : (
          <div className={`w-full bg-gradient-to-br from-[#8B0000] to-[#DC143C] flex items-center justify-center ${isList ? 'h-full' : 'h-56'}`}>
            <FaUserCircle className="text-white/40 text-7xl" />
          </div>
        )}
        {profile.isVerified && (
          <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 shadow">
            <FaCheckCircle className="text-[10px]" />
            <span>सत्यापित</span>
          </div>
        )}
        {profile.subscription === 'premium' && (
          <div className="absolute top-2 left-2 bg-[#FFD700] text-[#8B0000] text-xs px-2 py-1 rounded-full font-bold shadow">
            Premium
          </div>
        )}
      </div>

      {/* Info */}
      <div className={`p-4 ${isList ? 'flex-1 flex flex-col justify-between' : ''}`}>
        <div>
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-semibold text-[#8B0000] font-['Playfair_Display'] truncate">
              {profile.name || 'Profile'}
            </h3>
            <span className="text-xs text-gray-400 font-mono">#{profile.profileId || profile._id?.slice(-6)}</span>
          </div>

          <div className="space-y-1.5 text-sm text-gray-600">
            <p className="font-['Noto_Sans_Devanagari']">
              {profile.age ? `${profile.age} वर्षे` : ''}{profile.height ? ` | ${profile.height}` : ''}
            </p>
            {profile.caste && (
              <p className="font-['Noto_Sans_Devanagari'] text-xs text-gray-500">
                {profile.religion} - {profile.caste}
              </p>
            )}
            {profile.qualification && (
              <p className="flex items-center gap-1.5">
                <FaGraduationCap className="text-[#B8860B] text-xs" />
                <span className="truncate">{profile.qualification}</span>
              </p>
            )}
            {profile.occupation && (
              <p className="flex items-center gap-1.5">
                <FaBriefcase className="text-[#B8860B] text-xs" />
                <span className="truncate">{profile.occupation}</span>
              </p>
            )}
            {profile.city && (
              <p className="flex items-center gap-1.5">
                <FaMapMarkerAlt className="text-[#DC143C] text-xs" />
                <span className="truncate">{profile.city}{profile.state ? `, ${profile.state}` : ''}</span>
              </p>
            )}
          </div>

          {/* Contact - blurred for free users */}
          {!isPremium && (
            <div className="mt-2 relative">
              <div className="blur-sm select-none text-xs text-gray-400">
                +91 98XXXX1234 | email@example.com
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs text-[#8B0000] flex items-center gap-1 bg-[#FFF8F0] px-2 py-0.5 rounded-full border border-[#8B0000]/20">
                  <FaLock className="text-[8px]" /> Premium Only
                </span>
              </div>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-2 ${isList ? '' : 'mt-4'}`}>
          <Link
            to={`/profile/${profile._id}`}
            className="flex-1 bg-[#8B0000] text-white text-center py-2 rounded-lg text-sm font-medium hover:bg-[#DC143C] transition-colors"
          >
            प्रोफाइल पहा
          </Link>
          <button className="p-2 border border-[#DC143C] text-[#DC143C] rounded-lg hover:bg-[#DC143C] hover:text-white transition-colors">
            <FaHeart />
          </button>
        </div>
      </div>
    </div>
  );
};

// Filter Sidebar
const FilterSidebar = ({ filters, setFilters, onApply, isOpen, onClose }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const handleChange = (key, value) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    setFilters(localFilters);
    onApply(localFilters);
    onClose();
  };

  const handleReset = () => {
    const empty = {
      gender: '', ageMin: '', ageMax: '', religion: '', caste: '',
      city: '', state: '', qualification: '', occupation: '',
      incomeMin: '', incomeMax: '', maritalStatus: '', motherTongue: ''
    };
    setLocalFilters(empty);
    setFilters(empty);
    onApply(empty);
  };

  const religions = ['हिंदू', 'बौद्ध', 'जैन', 'ख्रिश्चन', 'मुस्लिम', 'शीख', 'इतर'];
  const qualifications = ['10th', '12th', 'Diploma', 'Graduate', 'Post Graduate', 'PhD', 'ITI', 'Other'];
  const maritalStatuses = ['अविवाहित', 'विधवा/विधुर', 'घटस्फोटित', 'विभक्त'];
  const states = ['Maharashtra', 'Karnataka', 'Goa', 'Madhya Pradesh', 'Gujarat', 'Other'];

  const SelectField = ({ label, value, options, onChange, placeholder }) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1 font-['Noto_Sans_Devanagari']">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#DC143C] focus:border-transparent outline-none"
      >
        <option value="">{placeholder || 'सर्व'}</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}

      <aside className={`
        fixed top-0 left-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 overflow-y-auto
        lg:relative lg:transform-none lg:shadow-md lg:rounded-xl lg:h-auto lg:w-full lg:z-auto
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-[#8B0000] font-['Noto_Sans_Devanagari'] flex items-center gap-2">
              <FaFilter className="text-sm" /> फिल्टर
            </h3>
            <button onClick={onClose} className="lg:hidden text-gray-500 hover:text-[#8B0000]">
              <FaTimes />
            </button>
          </div>

          <div className="space-y-4">
            {/* Gender */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 font-['Noto_Sans_Devanagari']">लिंग</label>
              <div className="flex gap-2">
                {[{ v: 'male', l: 'पुरुष' }, { v: 'female', l: 'स्त्री' }].map(g => (
                  <button
                    key={g.v}
                    onClick={() => handleChange('gender', localFilters.gender === g.v ? '' : g.v)}
                    className={`flex-1 py-2 text-sm rounded-lg border transition-colors font-['Noto_Sans_Devanagari'] ${
                      localFilters.gender === g.v
                        ? 'bg-[#8B0000] text-white border-[#8B0000]'
                        : 'border-gray-300 text-gray-600 hover:border-[#DC143C]'
                    }`}
                  >
                    {g.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Age Range */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 font-['Noto_Sans_Devanagari']">वय</label>
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  placeholder="Min"
                  value={localFilters.ageMin}
                  onChange={e => handleChange('ageMin', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#DC143C] outline-none"
                  min="18" max="70"
                />
                <span className="text-gray-400">-</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={localFilters.ageMax}
                  onChange={e => handleChange('ageMax', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#DC143C] outline-none"
                  min="18" max="70"
                />
              </div>
            </div>

            <SelectField label="धर्म" value={localFilters.religion} options={religions} onChange={v => handleChange('religion', v)} />
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 font-['Noto_Sans_Devanagari']">जात</label>
              <input
                type="text"
                placeholder="जात टाका"
                value={localFilters.caste}
                onChange={e => handleChange('caste', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#DC143C] outline-none"
              />
            </div>
            <SelectField label="वैवाहिक स्थिती" value={localFilters.maritalStatus} options={maritalStatuses} onChange={v => handleChange('maritalStatus', v)} />
            <SelectField label="शिक्षण" value={localFilters.qualification} options={qualifications} onChange={v => handleChange('qualification', v)} />

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 font-['Noto_Sans_Devanagari']">व्यवसाय</label>
              <input
                type="text"
                placeholder="व्यवसाय टाका"
                value={localFilters.occupation}
                onChange={e => handleChange('occupation', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#DC143C] outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 font-['Noto_Sans_Devanagari']">शहर</label>
              <input
                type="text"
                placeholder="शहर टाका"
                value={localFilters.city}
                onChange={e => handleChange('city', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#DC143C] outline-none"
              />
            </div>

            <SelectField label="राज्य" value={localFilters.state} options={states} onChange={v => handleChange('state', v)} />

            {/* Income Range */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 font-['Noto_Sans_Devanagari']">वार्षिक उत्पन्न (लाखात)</label>
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  placeholder="Min"
                  value={localFilters.incomeMin}
                  onChange={e => handleChange('incomeMin', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#DC143C] outline-none"
                />
                <span className="text-gray-400">-</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={localFilters.incomeMax}
                  onChange={e => handleChange('incomeMax', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#DC143C] outline-none"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-2">
            <button
              onClick={handleApply}
              className="w-full bg-[#8B0000] text-white py-2.5 rounded-lg font-medium hover:bg-[#DC143C] transition-colors font-['Noto_Sans_Devanagari']"
            >
              फिल्टर लागू करा
            </button>
            <button
              onClick={handleReset}
              className="w-full border border-gray-300 text-gray-600 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition-colors font-['Noto_Sans_Devanagari']"
            >
              रीसेट करा
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

const BrowseProfilesPage = () => {
  const { isPremium, api } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    gender: '', ageMin: '', ageMax: '', religion: '', caste: '',
    city: '', state: '', qualification: '', occupation: '',
    incomeMin: '', incomeMax: '', maritalStatus: '', motherTongue: ''
  });

  const fetchProfiles = useCallback(async (appliedFilters = filters, page = currentPage) => {
    setLoading(true);
    try {
      const params = { page, limit: 12, sort: sortBy };
      if (searchQuery) params.search = searchQuery;

      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });

      const response = await api.get('/api/search', { params });
      const data = response.data;
      setProfiles(data.profiles || data.data || []);
      setTotalResults(data.total || data.totalCount || 0);
      setTotalPages(data.totalPages || Math.ceil((data.total || 0) / 12) || 1);
    } catch (error) {
      console.error('Failed to fetch profiles:', error);
      setProfiles([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [api, sortBy, searchQuery, filters, currentPage]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleFilterApply = (newFilters) => {
    setCurrentPage(1);
    fetchProfiles(newFilters, 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchProfiles(filters, 1);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="min-h-screen bg-[#FFF8F0] flex flex-col">
      <Navbar />

      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B0000] to-[#DC143C] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Noto_Sans_Devanagari'] mb-2">
            प्रोफाइल शोधा
          </h1>
          <p className="text-[#FFD700]/80 text-sm font-['Playfair_Display']">Browse Profiles &amp; Find Your Perfect Match</p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mt-4 flex gap-2">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="नाव, शहर, व्यवसाय शोधा..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm focus:ring-2 focus:ring-[#FFD700] outline-none"
              />
            </div>
            <button
              type="submit"
              className="bg-[#FFD700] text-[#8B0000] px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-[#B8860B] hover:text-white transition-colors"
            >
              शोधा
            </button>
          </form>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1">
        <div className="flex gap-6">
          {/* Sidebar - Desktop */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <FilterSidebar
              filters={filters}
              setFilters={setFilters}
              onApply={handleFilterApply}
              isOpen={true}
              onClose={() => {}}
            />
          </div>

          {/* Mobile Filter Sidebar */}
          <FilterSidebar
            filters={filters}
            setFilters={setFilters}
            onApply={handleFilterApply}
            isOpen={filterOpen}
            onClose={() => setFilterOpen(false)}
          />

          {/* Results */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4 bg-white rounded-xl p-3 shadow-sm">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setFilterOpen(true)}
                  className="lg:hidden flex items-center gap-2 px-3 py-2 border border-[#8B0000] text-[#8B0000] rounded-lg text-sm hover:bg-[#8B0000] hover:text-white transition-colors"
                >
                  <FaFilter /> फिल्टर
                </button>
                <span className="text-sm text-gray-600 font-['Noto_Sans_Devanagari']">
                  <strong className="text-[#8B0000]">{totalResults}</strong> प्रोफाइल सापडले
                </span>
              </div>

              <div className="flex items-center gap-3">
                {/* Sort */}
                <div className="flex items-center gap-2">
                  <FaSortAmountDown className="text-gray-400 text-sm" />
                  <select
                    value={sortBy}
                    onChange={e => { setSortBy(e.target.value); setCurrentPage(1); }}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#DC143C] outline-none"
                  >
                    <option value="newest">Newest</option>
                    <option value="age_asc">Age (Low-High)</option>
                    <option value="age_desc">Age (High-Low)</option>
                  </select>
                </div>

                {/* View Toggle */}
                <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-[#8B0000] text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                  >
                    <FaTh />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-[#8B0000] text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                  >
                    <FaList />
                  </button>
                </div>
              </div>
            </div>

            {/* Profile Grid */}
            {loading ? (
              <div className={`grid gap-5 ${
                viewMode === 'grid'
                  ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'
                  : 'grid-cols-1'
              }`}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : profiles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-24 h-24 mx-auto mb-6 bg-[#8B0000]/10 rounded-full flex items-center justify-center">
                  <FaSearch className="text-[#8B0000] text-3xl" />
                </div>
                <h3 className="text-xl font-bold text-[#8B0000] font-['Noto_Sans_Devanagari'] mb-2">
                  कोणतेही प्रोफाइल सापडले नाहीत
                </h3>
                <p className="text-gray-500 font-['Noto_Sans_Devanagari']">
                  कृपया फिल्टर बदलून पुन्हा प्रयत्न करा
                </p>
              </div>
            ) : (
              <div className={`grid gap-5 ${
                viewMode === 'grid'
                  ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'
                  : 'grid-cols-1'
              }`}>
                {profiles.map(profile => (
                  <ProfileCard
                    key={profile._id}
                    profile={profile}
                    isPremium={isPremium}
                    viewMode={viewMode}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && !loading && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-[#8B0000] hover:text-white hover:border-[#8B0000] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FaChevronLeft />
                </button>

                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                      page === currentPage
                        ? 'bg-[#8B0000] text-white shadow-md'
                        : 'border border-gray-300 text-gray-600 hover:bg-[#FFF8F0] hover:border-[#8B0000] hover:text-[#8B0000]'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-[#8B0000] hover:text-white hover:border-[#8B0000] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BrowseProfilesPage;
